/**
 * Date utilities — single source of truth. The legacy `ownership-gov.js`
 * defines `toISOFromBaalut()` twice (copy-pasted), and `isoToYYYYMM()`
 * appears with a fallback in `vehicle-details.js`. Consolidated here.
 *
 * `baalut_dt` is the ownership-change date from data.gov.il. Observed formats:
 *   - YYYYMMDD numeric (most common):   "20230401"
 *   - YYYYMM numeric (when day omitted): "202304"
 *   - ISO string:                        "2023-04-01T00:00:00.000Z"
 *   - Hebrew-display string:             "01/04/2023"
 */

/**
 * Parse a `baalut_dt` value into an ISO UTC string, or null if unparseable.
 * Accepts numbers, strings, and strings with a leading `+`.
 * @param {unknown} val
 * @returns {string | null}
 */
export function parseBaalutToISO(val) {
  if (val == null) return null;
  let s = typeof val === 'number' ? String(val) : String(val).trim();
  s = s.replace(/^\+/, '').trim();
  if (!s) return null;

  if (/^\d{8}$/.test(s)) {
    const y = +s.slice(0, 4), m = +s.slice(4, 6), d = +s.slice(6, 8);
    return new Date(Date.UTC(y, m - 1, d)).toISOString();
  }
  if (/^\d{6}$/.test(s)) {
    const y = +s.slice(0, 4), m = +s.slice(4, 6);
    return new Date(Date.UTC(y, m - 1, 1)).toISOString();
  }
  // dd/mm/yyyy or dd-mm-yyyy or dd.mm.yyyy
  let m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) {
    const day = +m[1], mon = +m[2], year = +m[3];
    return new Date(Date.UTC(year, mon - 1, day)).toISOString();
  }
  // yyyy-mm-dd or yyyy/mm/dd at start
  m = s.match(/^(\d{4})[\/\-.](\d{1,2})(?:[\/\-.](\d{1,2}))?/);
  if (m) {
    const y = +m[1], mo = +m[2], d = m[3] ? +m[3] : 1;
    return new Date(Date.UTC(y, mo - 1, d)).toISOString();
  }
  // fallback: let Date try
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

/**
 * Convert an ISO string to a `YYYY-MM` calendar key.
 * @param {string | null | undefined} iso
 * @returns {string | null}
 */
export function isoToYearMonth(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Combine the two: `baalut_dt` → `YYYY-MM` directly.
 * @param {unknown} val
 * @returns {string | null}
 */
export function baalutToYearMonth(val) {
  return isoToYearMonth(parseBaalutToISO(val));
}

/**
 * Display formatter: ISO → `DD/MM/YYYY` (Hebrew/European order).
 * @param {string | null | undefined} iso
 * @returns {string}
 */
export function formatDDMMYYYY(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/**
 * Human time-ago: "לפני 5 דקות" / "אתמול" / "16/03/2024".
 * @param {number | string | Date} when
 * @returns {string}
 */
export function timeAgo(when) {
  const t = typeof when === 'number' ? when : new Date(when).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'עכשיו';
  if (minutes < 60) return `לפני ${minutes} דק׳`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'אתמול';
  if (days < 7) return `לפני ${days} ימים`;
  return formatDDMMYYYY(new Date(t).toISOString());
}

/**
 * Compute the end of the calendar quarter that contains the given ISO date.
 * Returns ISO UTC string for the last second of the quarter.
 * @param {string | null | undefined} iso
 * @returns {string | null}
 */
export function quarterEndISO(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const q = Math.floor(d.getUTCMonth() / 3); // 0..3
  const endMonth = q * 3 + 2; // last month of quarter
  // last day of endMonth: use day 0 of next month
  const last = new Date(Date.UTC(d.getUTCFullYear(), endMonth + 1, 0, 23, 59, 59));
  return last.toISOString();
}
